import React, {useCallback, useMemo} from "react";
import {ParentCaseAuthorizationListAction} from "../parentCaseAuthorizationReducer";
import {GroupedOptions, GroupedSearchDropdown, Option, showToast} from "common";
import {AuthClientReportModalState} from "../ClientProfileAuthorizationModal";

type AuthReportSubmitAction =
    'Report'
    | 'Staffing Plan'
    | 'Change Hours Addendum'
    | 'Client Return/Transfer Request'
    | 'Clinical Consult'
    | 'No Contact'
    | 'Auth Review Needed'
    | 'Treatment Hold'
    | 'Discharge Notification'
    | 'Missing Documentation'
    | 'Report Status Inquiry'
    | 'Client/Family Concern'
    | 'Insurance Notifications';

// All options sorted in ascending alphabetical order
const AUTH_REPORT_SUBMIT_ACTIONS: AuthReportSubmitAction[] = [
    'Auth Review Needed',
    'Change Hours Addendum',
    'Client Return/Transfer Request',
    'Client/Family Concern',
    'Clinical Consult',
    'Discharge Notification',
    'Treatment Hold',
    'Insurance Notifications',
    'Missing Documentation',
    'No Contact',
    'Report',
    'Report Status Inquiry',
    'Staffing Plan',
];

const ASSESSMENT_AUTH_REPORT_SUBMIT_ACTIONS: AuthReportSubmitAction[] = [
    'Auth Review Needed',
    'Change Hours Addendum',
    'Client Return/Transfer Request',
    'Client/Family Concern',
    'Clinical Consult',
    'Discharge Notification',
    'Treatment Hold',
    'Insurance Notifications',
    'Missing Documentation',
    'No Contact',
    'Report',
    'Report Status Inquiry',
    'Staffing Plan',
];

const THERAPY_AND_DE_AUTH_REPORT_SUBMIT_ACTIONS: AuthReportSubmitAction[] = [
    'Report',
];

// Helper function to group options by category
function groupAuthReportOptions(
    options: AuthReportSubmitAction[],
    preventSelectOptionAndMessage: Map<string, string>
): GroupedOptions {
    // Group actions by category
    const grouped: GroupedOptions = {
        'Reports': [],
        'Requests': []
    };

    options.forEach(option => {
        const optionData: Option = {
            disable: preventSelectOptionAndMessage.has(option),
            label: option,
            value: option
        };

        // Categorize options
        if (option === 'Report' || option === 'Staffing Plan' || option === 'Change Hours Addendum') {
            grouped['Reports'].push(optionData);
        } else {
            grouped['Requests'].push(optionData);
        }
    });

    // Remove empty groups
    Object.keys(grouped).forEach(key => {
        if (grouped[key].length === 0) {
            delete grouped[key];
        }
    });

    return grouped;
}

type Props = {
    dispatch: React.Dispatch<ParentCaseAuthorizationListAction>
    isAssessmentAuth: boolean;
    isTherapyAuth: boolean;
    isDEAuth: boolean;
    modalState: AuthClientReportModalState;
    preventSelectOptionAndMessage?: Map<string, string>;
}

export function AuthReportSubmissionDropdown({
                                                 dispatch,
                                                 isAssessmentAuth,
                                                 isDEAuth,
                                                 isTherapyAuth,
                                                 modalState,
                                                 preventSelectOptionAndMessage = new Map<string, string>(),
                                             }: Props) {

    const handleAction = useCallback((action: AuthReportSubmitAction) => {
        const actionMap: Record<AuthReportSubmitAction, () => void> = {
            "Report": () => dispatch({type: "SHOW_NEW_REPORT_MODAL"}),
            "Staffing Plan": () => modalState.showStaffingPlan$.next(true),
            "Change Hours Addendum": () => modalState.showAddendum$.next(true),
            "Clinical Consult": () => modalState.showConsultWithCCM$.next(true),
            "Client Return/Transfer Request": () => modalState.showClientReturnTransferRequest$.next(true),
            "Treatment Hold": () => modalState.showTreatmentHold$.next(true),
            "No Contact": () => modalState.showNoContact$.next(true),
            "Auth Review Needed": () => modalState.showAuthReviewNeeded$.next(true),
            "Discharge Notification": () => modalState.showAnticipatedDischargeDateUpdate$.next(true),
            "Client/Family Concern": () => modalState.showClientFamilyConcern$.next(true),
            "Insurance Notifications": () => modalState.showInsuranceNotification$.next(true),
            "Missing Documentation": () => modalState.showMissingDocumentation$.next(true),
            "Report Status Inquiry": () => modalState.showReportStatusInquiry$.next(true),
        };

        actionMap[action]?.();
    }, [dispatch, modalState]);

    let options = AUTH_REPORT_SUBMIT_ACTIONS;
    if (isAssessmentAuth) {
        options = ASSESSMENT_AUTH_REPORT_SUBMIT_ACTIONS;
    } else if (isTherapyAuth || isDEAuth) {
        options = THERAPY_AND_DE_AUTH_REPORT_SUBMIT_ACTIONS;
    }

    if (process.env.REACT_APP_AP_ENABLED !== 'true') {
        options = options.filter(option => option !== 'Discharge Notification');
    }

    // Transform options to grouped format using helper function
    const groupedOptions = useMemo(() => 
        groupAuthReportOptions(options, preventSelectOptionAndMessage),
        [options, preventSelectOptionAndMessage]
    );

    return (
        <GroupedSearchDropdown
            options={groupedOptions}
            selected={''}
            setSelected={(selected) => {
                // Check if option is prevented
                if (preventSelectOptionAndMessage.has(selected)) {
                    showToast.warning(preventSelectOptionAndMessage.get(selected)!, {duration: 15});
                    return;
                }
                handleAction(selected as AuthReportSubmitAction);
            }}
            maxHeight={"350px"}
            textColor={"black-200"}
            groupTextColor={"marron"}
            placeholder='Submit'
            isButton
            searchPlaceholder="Search actions..."
            emptySearchMessage="No actions found"
        />
    );
}